import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from "@angular/forms";
import { Subscription, of, Observable, forkJoin, interval } from "rxjs";
import { tap, concatMap, map, mergeMap } from "rxjs/operators";
import { DocumentManagementService } from "../../../services/document-management.service";
import { OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeyword, OnBaseKeywordQuery, OnBaseQueryModel, keywordEquals, KeywordRelation } from "../../../models/onBaseIntegrationAndMapping.model";
import { deepClone } from "../../../../builder/models/editor.model";
import { SortAttribute } from "../../../../core/models/sort.model";
import { Option } from "../../../models/flow.dto";
import { AuthService } from "../../../../services/auth.service";

@Component({
    selector: 'document-search-options',
    templateUrl: './document-search-options.component.html',
    styleUrls: ['./document-search-options.component.scss'],
    standalone: false
})
export class DocumentSearchOptionsComponent implements OnInit, OnDestroy {

    allDocumentTypes: OnBaseDocumentType[];
    allKeywords: OnBaseKeyword[];
    availableKeywords: OnBaseKeyword[];
    confidentialKeyword: OnBaseKeyword;
    documentGroups: OnBaseDocumentGroup[];
    documentTypes: OnBaseDocumentType[];
    documentTypeInfoBubble: string = "To add individual document types, first select a document group.";
    maxResults: number = 1000;

    get disableSearch(): boolean {
        if (this.formGroup == null) return true;
        return this.forceDisableSearch || (this.selectedDocumentTypes.length === 0 && this.selectedKeywords.length === 0 && this.fromDate == null && this.toDate == null);
    }

    get confidentialQueryArgument(): OnBaseKeywordQuery {
        if (this.confidentialKeyword == null) return null;
        return new OnBaseKeywordQuery(this.confidentialKeyword.id, this.confidentialKeyword.name, "YES", "ne");
    }
    

    equalityOptions: Option[] = [
        { id: "eq", text: " =" } as Option,
        { id: "ne", text: "<>" } as Option,
        { id: "gt", text: " >" } as Option,
        { id: "lt", text: " <" } as Option,
        { id: "gte", text: ">=" } as Option,
        { id: "lte", text: "<=" } as Option
    ];

    @Input() forceDisableSearch: boolean = false;

    formGroup: FormGroup;

    get fromDate(): Date | null {
        let dt = null;
        if (this.formGroup != null) {
            const fromCtrl = this.formGroup.get("fromDate");
            if (fromCtrl != null && fromCtrl.value != null && fromCtrl.value != "") {
                dt = new Date(fromCtrl.value);
            }
        }
        return dt;
    }


    groupTypeMap: Map<number, OnBaseDocumentType[]> = new Map<number, OnBaseDocumentType[]>();

    helpText: string = "Using keywords of different types will require all of those conditions to match in any returned documents.";

    relationOptions: any[] = [
        { id: "", text: "--"},
        { id: "AND", text: "And" },
        { id: "OR", text: "Or" }
    ];

    @Input() requiredKeywords: OnBaseKeywordQuery[];

    requiredWarning: string = "Alternate values for keywords that are required are not allowed.";

    @Output() runSearch: EventEmitter<OnBaseQueryModel> = new EventEmitter<OnBaseQueryModel>();
    selectDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    selectDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };
    selectedDocumentTypes: OnBaseDocumentType[] = [];
    selectedKeywords: OnBaseKeywordQuery[] = [];

    selectKeyword: OnBaseKeyword = { id : -1, name: "-- Select a Keyword -- ", dataLength: 0, dataType: "string" };
    subscription: Subscription = new Subscription();

    @Output() throwError: EventEmitter<any> = new EventEmitter<any>();

    get toDate(): Date | null {
        let dt = null;
        if (this.formGroup != null) {
            const toCtrl = this.formGroup.get("toDate");
            if (toCtrl != null && toCtrl.value != null && toCtrl.value != "") {
                dt = new Date(toCtrl.value);
            }
        }
        return dt;
    }
    typeKeywordMap: Map<number, OnBaseKeyword[]> = new Map<number, OnBaseKeyword[]>();


    constructor(private readonly builder: FormBuilder, private readonly documentService: DocumentManagementService, private readonly auth: AuthService) { }

    ngOnInit() {
        this.buildForm();
        this.groupTypeMap.set(this.selectDocumentGroup.id, [this.selectDocumentType]);
        this.typeKeywordMap.set(this.selectDocumentType.id, [this.selectKeyword]);
        const documentGroup$: Observable<OnBaseDocumentGroup[]> = this.documentService.getAllDocumentGroups().pipe(
            tap((groups: OnBaseDocumentGroup[]) => {
                this.documentGroups = groups;
                if (this.documentGroups.findIndex(g => g.id === this.selectDocumentGroup.id) === -1) {
                    this.documentGroups.unshift(this.selectDocumentGroup);
                }
            })
        );
        const documentType$: Observable<OnBaseDocumentType[]> = this.documentService.getAllDocumentTypes().pipe(
            tap((types: OnBaseDocumentType[]) => {
                this.allDocumentTypes = types;
                if (this.allDocumentTypes.findIndex(g => g.id === this.selectDocumentType.id) === -1) {
                    this.allDocumentTypes.unshift(this.selectDocumentType);
                }
                this.groupTypeMap.set(this.selectDocumentGroup.id, [ this.selectDocumentType ]);
                this.documentTypes = this.groupTypeMap.get(-1);
            })
        )
        const keyword$: Observable<OnBaseKeyword[]> = this.documentService.getAllKeywords().pipe(
            tap((words: OnBaseKeyword[]) => {
                if (this.auth.User.isInternal == false) {
                    words = words.filter(w => w.name.toLocaleLowerCase() !== "dogrm - confidential"); //remove the confidential keyword as an option for external users as it'll get overridden anyway
                }
                this.allKeywords = words;
                this.allKeywords.unshift(this.selectKeyword);
                this.typeKeywordMap.set(this.selectKeyword.id, deepClone(this.allKeywords));
            })
        );
        let dataObs$ = forkJoin(documentGroup$, documentType$, keyword$).pipe(
            tap(([groups, types, keywords]) => {
                this.documentGroups = <OnBaseDocumentGroup[]>groups;
                if (this.documentGroups.findIndex(g => g.id === this.selectDocumentGroup.id) === -1) {
                    this.documentGroups.unshift(this.selectDocumentGroup);
                }
                this.allDocumentTypes = <OnBaseDocumentType[]>types;
                this.groupTypeMap.set(this.selectDocumentGroup.id, [this.selectDocumentType]);
                if (this.allDocumentTypes.findIndex(g => g.id === this.selectDocumentType.id) === -1) {
                    this.allDocumentTypes.unshift(this.selectDocumentType);
                }
                this.allKeywords = <OnBaseKeyword[]>keywords;
                this.typeKeywordMap.set(this.selectDocumentType.id, deepClone(this.allKeywords));
            }),
            concatMap(() => {
                return this.loadAvailableKeywords(-1).pipe(tap((words: OnBaseKeyword[]) => {
                    this.availableKeywords = deepClone(words);
                    if (this.availableKeywords.findIndex(k => k.id === -1) === -1) {
                        this.availableKeywords.unshift(this.selectKeyword);
                    }
                }));
            }));
        let data$: Subscription = dataObs$.subscribe(
                () => {

                },
                (error) => {
                    console.error(error);
                    this.handleError(error);
                }
            );

        this.subscription.add(data$);       
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    addAllTypes(): void {
        const validTypes = this.documentTypes.filter(d => d.id !== this.selectDocumentType.id);
        validTypes.forEach(t => this.selectedDocumentTypes.push(t));        
    }

    addDocumentType(): void {
        const typeCtrl = this.formGroup.get("documentType");
        if (typeCtrl != null) {
            let value = parseInt(typeCtrl.value);
            if (value !== this.selectDocumentType.id) {
                let type = this.allDocumentTypes.find(t => t.id === value);
                if (type != null) {
                    this.selectedDocumentTypes.push(type);
                }
            }
        }
    }

    addKeywordCriteria() {
        const keywordGroup = <FormGroup>this.formGroup.get("keywordCriteria");
        if (keywordGroup != null) {
            const keywordCtrl = keywordGroup.get("selectedKeyword");
            const keywordValueCtrl = keywordGroup.get("keywordValue");
            const equalityCtrl = keywordGroup.get("equality");
            const relationCtrl = keywordGroup.get("relation");
            if (keywordCtrl != null && keywordValueCtrl != null && equalityCtrl != null && relationCtrl != null) {
                const keywordId = Number(keywordCtrl.value);
                const rawValue = keywordValueCtrl.value;
                const keyword: OnBaseKeyword = this.availableKeywords.find(k => k.id === keywordId);
                if (keyword != null) {
                    const searchValue = this.typeCastKeywordValue(keyword, rawValue);
                    const queryItem: OnBaseKeywordQuery = new OnBaseKeywordQuery(keyword.id, keyword.name, searchValue, equalityCtrl.value.toString());  
                    queryItem.relation = <KeywordRelation>relationCtrl.value;
                    this.selectedKeywords.push(queryItem);
                }                
            }
            keywordGroup.reset();
            if (equalityCtrl != null) {
                equalityCtrl.patchValue("eq");
            }
            if (relationCtrl != null) {
                relationCtrl.patchValue("OR");
            }
            
        }   
    }

    buildForm(): void {
        this.formGroup = this.builder.group({
            fromDate: ["", []],
            toDate: [new Date(), []],
            documentGroup: [this.selectDocumentGroup.id, []],
            documentType: [this.selectDocumentType.id, [Validators.min(0)]],            
            keywordCriteria: this.builder.group({
                selectedKeyword: [this.selectKeyword.id, [Validators.min(0)]],
                equality: ["eq", [Validators.required]],
                keywordValue: ["", [Validators.required]],
                relation: ["OR", []]
            })
        });
    }

    cleanseKeywordGroup(): void {
        const removeList: OnBaseKeywordQuery[] = [];
        for (let k = 0; k < this.selectedKeywords.length; k++) {
            const unavailable: boolean = this.availableKeywords.findIndex(a => a.id === this.selectedKeywords[k].keywordTypeId) === -1;
            if (unavailable) {
                removeList.push(this.selectedKeywords[k]);
            }
        }
        if (removeList.length > 0) {
            removeList.forEach(k => {
                this.removeKeywordCriteria(k);
            });
        }
    }

    documentGroupChanged(evt: Event): void {   
        let groupCtrl = this.formGroup.get("documentGroup");
        if (groupCtrl != null && groupCtrl.value != null) {
            groupCtrl.disable();
            this.documentTypes = null;
            let typeCtrl = this.formGroup.get("documentType");
            if (typeCtrl != null) {
                typeCtrl.patchValue(this.selectDocumentType.id);
                typeCtrl.disable();
            }
            let wordGroup = this.formGroup.get("keywordCriteria");
            if (wordGroup != null) wordGroup.disable();
            let obs$ = this.loadTypesByGroup(parseInt(groupCtrl.value)).pipe(
                tap((types: OnBaseDocumentType[]) => {
                    this.groupTypeMap.set(parseInt(groupCtrl.value), types);
                    this.documentTypes = deepClone(types);
                    if (types.findIndex(t => t.id === this.selectDocumentType.id) === -1) {
                        this.documentTypes.unshift(this.selectDocumentType);
                    }
                })
            ).subscribe(
                () => {
                    groupCtrl.enable();
                    if (typeCtrl != null) typeCtrl.enable();
                    if (wordGroup != null) wordGroup.enable();
                },
                (error) => {
                    console.error(error);
                    this.handleError(error);
                }
            );
            this.subscription.add(obs$);
        }
    }

    documentTypeChanged(evt: Event): void {  
        let typeCtrl = this.formGroup.get("documentType");
        if (typeCtrl != null && typeCtrl.value != null) {
            typeCtrl.disable();
            this.availableKeywords = null;
            let value = parseInt(typeCtrl.value);
            const keywordGroup = this.formGroup.get("keywordCriteria");
            if (keywordGroup != null) {
                keywordGroup.get("selectedKeyword").patchValue(this.selectKeyword.id);
                keywordGroup.disable();
            }  
            const groupCtrl = this.formGroup.get("documentGroup");
            if (groupCtrl != null) groupCtrl.disable();
            const obs$ = this.loadAvailableKeywords(value).pipe(
                tap((words: OnBaseKeyword[]) => {
                    this.typeKeywordMap.set(value, words);
                    this.availableKeywords = deepClone(words);
                    if (words.findIndex(w => w.id === this.selectKeyword.id) === -1) {
                        this.availableKeywords.unshift(this.selectKeyword);
                    }
                })).subscribe(
                    () => {
                        this.cleanseKeywordGroup();
                        typeCtrl.enable();
                        if (keywordGroup != null) keywordGroup.enable();
                        if (groupCtrl != null) groupCtrl.enable();
                    },
                    (error) => {
                        console.error(error);
                        this.handleError(error);
                    }
            );
            this.subscription.add(obs$);            
        }        
    }

    fromDateChanged(newDate: Date): void {
        let fromCtrl = this.formGroup.get("fromDate");
        if (fromCtrl != null) {
            fromCtrl.patchValue(newDate);
        }
    }

    getDocumentTypes(): OnBaseDocumentType[] {
        let value = parseInt(this.formGroup.get("documentType").value);
        if (this.groupTypeMap.has(value)) {
            let types = this.groupTypeMap.get(value);
            return deepClone(types);
        }
        return [];
    }

    handleError(error: any): void {
        this.throwError.emit(error);
    }

    loadAvailableKeywords(typeId: number): Observable<OnBaseKeyword[]> {
        let obs$: Observable<OnBaseKeyword[]>;
        if (this.typeKeywordMap.has(typeId)) {
            obs$ = of(this.typeKeywordMap.get(typeId));
        } else {
            obs$ = this.documentService.getKeywordTypesByDocumentType(typeId);
        }
        if (this.requiredKeywords != null && this.requiredKeywords.length > 0) {
            obs$ = obs$.pipe(map((words: OnBaseKeyword[]) => {
                this.confidentialKeyword = words.find(w => w.name.toLocaleLowerCase() === "dogrm - confidential");
                if (this.auth.User.isInternal == false && this.confidentialKeyword != null) {
                    words = words.filter(w => w.name.toLocaleLowerCase() != this.confidentialKeyword.name.toLocaleLowerCase()); //remove the confidential keyword as an option for external users as it'll get overridden anyway
                }
                for (let r = 0; r < this.requiredKeywords.length; r++) {
                    let index = words.findIndex(w => w.name.toLocaleLowerCase() === this.requiredKeywords[r].keywordTypeName.toLocaleLowerCase());
                    if (index !== -1) {
                        words.splice(index, 1);
                    }
                }
                return words;
            }))
        }
        return obs$;
    }

    loadTypesByGroup(groupId: number): Observable<OnBaseDocumentType[]> {
        let obs$: Observable<OnBaseDocumentType[]>;
        if (this.groupTypeMap.has(groupId)) {
            obs$ = of(this.groupTypeMap.get(groupId));
        } else {
            obs$ = this.documentService.getDocumentTypesByDocumentGroup(groupId);
        }
        return obs$;
    }

    removeDocumentType(typeId: number): void {
        let index = this.selectedDocumentTypes.findIndex(t => t.id === typeId);
        if (index !== -1) {
            this.selectedDocumentTypes.splice(index, 1);
        }
    }

    removeKeywordCriteria(word: OnBaseKeywordQuery): void {
        const idx = this.selectedKeywords.findIndex(k => keywordEquals(word, k));
        if (idx !== -1) {
            this.selectedKeywords.splice(idx, 1);
        }      
    }

    search(): void {       
        const docTypes: number[] = [];
        const docTypeNames: string[] = [];
        for (let t = 0; t < this.selectedDocumentTypes.length; t++) {
            docTypes.push(this.selectedDocumentTypes[t].id);
            docTypeNames.push(this.selectedDocumentTypes[t].name);
        }
        if (this.requiredKeywords == null) this.requiredKeywords = [];
        const query = {
            documentTypeIds: docTypes,
            documentTypeNames: docTypeNames,
            resultsPerPage: 25,
            maxResults: this.maxResults,
            pageNumber: 1,
            searchParameters: this.requiredKeywords.concat(this.selectedKeywords),
            sort: SortAttribute.DocumentID,
            isAscending: false
        } as OnBaseQueryModel;
        if (this.fromDate != null) {
            query.fromDate = this.fromDate;
        }
        if (this.toDate != null) {
            query.toDate = this.toDate;
        }
        if (this.auth.User.isInternal === false && this.confidentialKeyword != null) {
            if (query.searchParameters.find(q => q.keywordTypeId === this.confidentialKeyword.id) == null) {
                query.searchParameters.push(this.confidentialQueryArgument);
            }
        }
        this.runSearch.emit(query);
    }

    toDateChanged(newDate: Date): void {
        let toCtrl = this.formGroup.get("toDate");
        if (toCtrl != null) {
            toCtrl.patchValue(newDate);
        }
    }

    typeCastKeywordValue(word: OnBaseKeyword, value: any): any {
        let castValue: any = null;
        switch (word.dataType) {
            case "Numeric9":
            case "Numeric20":
                castValue = parseInt(value, 10);
                break;
            case "FloatingPoint":
                castValue = parseFloat(value);
                break;
            case "Date":
            case "DateTime":
                castValue = new Date(value);
            default:
                const maxLength = word.dataLength;
                if (value.toString().length > maxLength) {
                    castValue = value.toString().substring(0, maxLength - 1);
                } else {
                    castValue = value;
                }
                break;
        }
        return castValue;
    }
}
